<template>
  <transition name="ot-fade">
    <aside
      v-if="active"
      class="ot-card"
      role="dialog"
      aria-modal="false"
      aria-label="Welcome tour"
    >
      <button class="ot-skip" type="button" @click="finish">Skip tour</button>

      <div class="ot-icon" v-html="step.icon"></div>
      <h2 class="ot-title">{{ step.title }}</h2>
      <p class="ot-body">{{ step.body }}</p>

      <div class="ot-dots">
        <span
          v-for="(s, i) in steps"
          :key="i"
          class="ot-dot"
          :class="{ 'ot-dot-active': i === index }"
        ></span>
      </div>

      <div class="ot-actions">
        <button
          v-if="index > 0"
          type="button"
          class="ot-btn ot-btn-ghost"
          @click="back"
        >
          Back
        </button>
        <button type="button" class="ot-btn ot-btn-primary" @click="next">
          {{ isLast ? "Done — back to dashboard" : "Next: " + nextTitle }}
        </button>
      </div>
    </aside>
  </transition>
</template>

<script>
// A one-time, guided walkthrough — distinct from FirstVisitGuide.vue (which
// shows a small tip the first time you *organically* land on a page).
// This one drives navigation itself: each step routes to the real page
// it's talking about, shows a non-blocking card over it (the page stays
// fully visible — the point of a tour is to show the page, not hide it
// behind a modal), and the final step returns to the dashboard.
//
// Copy comes straight from pagesGuide.js (title + summary + first tip) so
// there's still exactly one place that describes what a page does —
// shared with FirstVisitGuide.vue. See docs/ARCHITECTURE.md.
//
// Mounted once, globally, in App.vue (it has to survive route changes,
// since it causes them). Starts automatically the first time a logged-in
// user's route becomes /dashboard, tracked via localStorage.rk_onboarded_tour
// (per-browser, not per-account — see docs/KNOWN_ISSUES.md).
import { pageGuides } from "./pagesGuide.js";
import { tourState } from "./tourState.js";

const STORAGE_KEY = "rk_onboarded_tour";
const SEEN_GUIDES_KEY = "rk_seen_guides"; // shared with FirstVisitGuide.vue

const HOUSE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9.5 20v-6h5v6"/></svg>';
const SPEEDOMETER_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="13" r="8"/><path d="M12 13 15.5 9"/><path d="M9 5.5 9.6 5.3M15 5.5l-.6-.2M5.5 9l.2.6M18.5 9l-.2.6"/></svg>';
const PEOPLE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 19c.8-3.4 3.3-5.2 6.5-5.2s5.7 1.8 6.5 5.2"/><circle cx="17.5" cy="9" r="2.6"/><path d="M15.7 13.9c2.5.3 4.2 2 4.8 4.6"/></svg>';
const CASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="6.5" width="19" height="12" rx="2"/><circle cx="12" cy="12.5" r="3"/><path d="M6 9v0M18 16v0"/></svg>';
const CHAT_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3.5 12c0-4.7 3.8-8 8.5-8s8.5 3.3 8.5 8-3.8 8-8.5 8c-1 0-2-.15-2.9-.44L4 21l1.2-3.6C3.9 16 3.5 14.1 3.5 12Z"/></svg>';
const BOOK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 4.5c2.5-1 5.5-1 8 0v15c-2.5-1-5.5-1-8 0Z"/><path d="M20 4.5c-2.5-1-5.5-1-8 0v15c2.5-1 5.5-1 8 0Z"/></svg>';

// Each step navigates to `path` and describes the page at `guideKey`
// (looked up in pagesGuide.js). Keep this list to pages that need no
// dynamic ID in the URL, so the tour never lands on a broken route.
const STEPS = [
  { path: "/dashboard", guideKey: "DashBoard", icon: SPEEDOMETER_ICON },
  { path: "/houses", guideKey: "HousesList", icon: HOUSE_ICON },
  { path: "/tenants", guideKey: "TenantsList", icon: PEOPLE_ICON },
  { path: "/all/payments", guideKey: "Payments", icon: CASH_ICON },
  { path: "/tenants/sms", guideKey: "tenantsSms", icon: CHAT_ICON },
  { path: "/help", guideKey: "Help", icon: BOOK_ICON },
];

export default {
  name: "OnboardingTour",
  data() {
    return {
      active: false,
      index: 0,
      steps: STEPS,
    };
  },
  computed: {
    step() {
      const s = this.steps[this.index];
      const guide = pageGuides[s.guideKey] || {};
      const body = [guide.summary, guide.tips && guide.tips[0]]
        .filter(Boolean)
        .join(" ");
      return {
        icon: s.icon,
        title:
          this.index === 0
            ? "Welcome to Rental Kenya"
            : guide.title || s.guideKey,
        body:
          this.index === 0
            ? "A quick walkthrough before you dive in — each step takes you to a real page and tells you what's on it. " +
              (guide.summary || "")
            : body,
      };
    },
    isLast() {
      return this.index === this.steps.length - 1;
    },
    nextTitle() {
      if (this.isLast) return "";
      const next = this.steps[this.index + 1];
      return (pageGuides[next.guideKey] || {}).title || "";
    },
  },
  watch: {
    "$route.path": {
      immediate: true,
      handler(path) {
        if (this.active) return; // mid-tour navigation, not a fresh arrival
        if (path !== "/dashboard") return;
        if (this.isOnboarded() || !this.isLoggedIn()) return;
        this.start();
      },
    },
  },
  methods: {
    isOnboarded() {
      try {
        return !!localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return true; // localStorage unavailable — don't force the tour
      }
    },
    isLoggedIn() {
      try {
        return !!localStorage.getItem("token");
      } catch (e) {
        return false;
      }
    },
    markSeen(guideKey) {
      // So FirstVisitGuide's own tip doesn't repeat right after the tour
      // already covered that page.
      try {
        const seen = JSON.parse(localStorage.getItem(SEEN_GUIDES_KEY) || "[]");
        if (!seen.includes(guideKey)) {
          seen.push(guideKey);
          localStorage.setItem(SEEN_GUIDES_KEY, JSON.stringify(seen));
        }
      } catch (e) {
        // ignore
      }
    },
    start() {
      this.index = 0;
      this.active = true;
      tourState.active = true;
      this.markSeen(this.steps[0].guideKey);
    },
    async goToStep(i) {
      this.index = i;
      this.markSeen(this.steps[i].guideKey);
      if (this.$route.path !== this.steps[i].path) {
        await this.$router.push(this.steps[i].path);
      }
    },
    next() {
      if (this.isLast) {
        this.finish();
      } else {
        this.goToStep(this.index + 1);
      }
    },
    back() {
      if (this.index > 0) this.goToStep(this.index - 1);
    },
    finish() {
      this.active = false;
      tourState.active = false;
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch (e) {
        // ignore — worst case the tour offers again next visit
      }
      if (this.$route.path !== "/dashboard") {
        this.$router.push("/dashboard");
      }
    },
    // Called by FaqPage.vue's "Replay welcome tour & tips" button, after
    // it clears rk_onboarded_tour and routes to /dashboard — the watcher
    // above then starts this automatically. Kept as a named method in case
    // something wants to trigger it directly while already on /dashboard.
    replay() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }
      if (this.$route.path === "/dashboard") {
        this.start();
      } else {
        this.$router.push("/dashboard");
      }
    },
  },
};
</script>

<style scoped>
.ot-card {
  --ot-text: #1f2a24;
  --ot-muted: #5b6b63;
  --ot-accent: #2f6f5e;
  --ot-accent-dark: #234f42;
  --ot-border: #dce3df;

  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 14px;
  padding: 22px 24px 18px;
  color: var(--ot-text);
  box-shadow: 0 16px 46px rgba(15, 23, 19, 0.24);
  border: 1px solid var(--ot-border);
  z-index: 3000;
}

.ot-skip {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  color: var(--ot-muted);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
}

.ot-skip:hover {
  color: var(--ot-text);
}

.ot-icon {
  color: var(--ot-accent);
  margin-bottom: 8px;
}

.ot-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 6px;
  padding-right: 70px;
}

.ot-body {
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ot-muted);
  margin: 0 0 16px;
}

.ot-dots {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
}

.ot-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ot-border);
}

.ot-dot-active {
  background: var(--ot-accent);
  width: 18px;
  border-radius: 3px;
}

.ot-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.ot-btn {
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.ot-btn-primary {
  background: var(--ot-accent);
  color: #ffffff;
}

.ot-btn-primary:hover {
  background: var(--ot-accent-dark);
}

.ot-btn-ghost {
  background: none;
  border: 1px solid var(--ot-border);
  color: var(--ot-text);
}

.ot-btn-ghost:hover {
  border-color: var(--ot-accent);
}

.ot-fade-enter-active,
.ot-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.ot-fade-enter-from,
.ot-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

@media (max-width: 480px) {
  .ot-card {
    bottom: 12px;
    width: calc(100% - 24px);
    padding: 18px 18px 16px;
  }
}
</style>
